import { useState } from 'react';
import { Permissions } from '../../../../utils/permissions';
import { Constants } from '../../../../utils/constants';
import { formatCount } from '../../../../utils/money';
import { useAuth } from '../../../../AuthContext';
import { useServerTable, wholeDayDates } from '../../../../hooks/useServerTable';
import { useExport } from '../../../../hooks/useExport';
import {
  Alert,
  AmountRangeFilter,
  Button,
  DataTable,
  DateRangeFilter,
  ExportButton,
  FilterSelect,
  Pagination,
  SearchInput,
  TableCard,
} from '../../../ui';
import tapTransactionColumns from '../dashboard/tapTransactionColumns';
import StoreSelect from './StoreSelect';
import EnterpriseDeactivated, { isEnterpriseDeactivated } from './EnterpriseDeactivated';

const TransactionsTable = ({ endpoint, exportEndpoint, showStore = false, stores = [] }) => {
  const { hasPermission } = useAuth();
  const canExport = hasPermission(Permissions.enterpriseTransactionsExport);
  const exporter = useExport(exportEndpoint, 'We could not export these transactions. Please try again.');

  const [merchantId, setMerchantId] = useState('');
  const [amount, setAmount] = useState({ min: '', max: '' });
  // SearchInput keeps its own text, so clearing remounts it.
  const [searchKey, setSearchKey] = useState(0);

  const table = useServerTable(endpoint, {
    sort: '-created_at',
    filters: { status: '' },
    dateParams: wholeDayDates,
    extraParams: {
      merchant_id: merchantId,
      'filter[amount][from]': amount.min,
      'filter[amount][to]': amount.max,
    },
    fallbackError: 'We could not load these transactions. Please try again.',
  });

  if (isEnterpriseDeactivated(table.requestError)) return <EnterpriseDeactivated />;

  const filtered = Boolean(
    table.search || table.filters.status || table.from || table.to || amount.min || amount.max || merchantId
  );

  const clearFilters = () => {
    table.setSearch('');
    table.setFilter('status', '');
    table.setFrom('');
    table.setTo('');
    setAmount({ min: '', max: '' });
    setMerchantId('');
    setSearchKey((key) => key + 1);
  };

  const columns = tapTransactionColumns({
    showStore,
    dateFilter: (
      <DateRangeFilter
        label="Date"
        from={table.from}
        to={table.to}
        onFromChange={table.setFrom}
        onToChange={table.setTo}
      />
    ),
    statusFilter: (
      <FilterSelect
        value={table.filters.status ?? ''}
        onChange={(value) => table.setFilter('status', value)}
        options={Constants.transactionStatuses}
        label="All"
        ariaLabel="Filter by status"
      />
    ),
  });

  return (
    <>
      {exporter.error && (
        <Alert variant="danger" onDismiss={exporter.clearError} className="mb-6">
          {exporter.error}
        </Alert>
      )}
      <TableCard
        title="Transactions"
        summary={{ label: 'Transactions', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <>
            {showStore && stores.length > 0 && (
              <StoreSelect stores={stores} value={merchantId} onChange={setMerchantId} />
            )}
            <AmountRangeFilter min={amount.min} max={amount.max} onChange={setAmount} />
            <SearchInput
              key={searchKey}
              value={table.search}
              onChange={table.setSearch}
              placeholder="Search reference or payer"
            />
            {filtered && (
              <Button variant="link-secondary" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            {canExport && (
              <ExportButton
                onClick={() => exporter.runExport(table.params)}
                loading={exporter.exporting}
                disabled={table.loading || table.total === 0 || table.invalidDateRange}
                title={`Exports all ${formatCount(table.total)} matching transactions, not just this page`}
              />
            )}
          </>
        }
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(row) => row.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{ title: 'No transactions', message: 'No transactions found for the selected filters.' }}
        />
      </TableCard>
    </>
  );
};

export default TransactionsTable;
